import { useForm } from "@formiz/core";
import { CharacterFlow, IReportCharacterRequest, NotificationType, useLoadCharacter, useTranslation } from "@next-platform/client-common-core";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { PageLayout, PageSize } from "@/components";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import useToast from "@/hooks/useToast";

import ReportFooter from "./components/ReportFooter";
import ReportForm from "./components/ReportForm";
import ReportHeader from "./components/ReportHeader";

const Report = () => {
  const reportForm = useForm();
  const { isValid, values } = reportForm;
  const { characterId } = useParams();
  const [stateSuccessMessage, setStateSuccessMessage] = useState<string | null>(null);
  const [stateIsFormSubmitted, setStateIsFormSubmitted] = useState(false);
  const [stateIsSubmitAttempted, setStateIsSubmitAttempted] = useState(false);
  const hasFormChanges = useMemo(() => Object.values(values).some((value) => value !== null && value !== undefined && value !== ""), [values]);

  const { t } = useTranslation();
  const { displayToast } = useToast();
  const {
    characterData: character,
    onUserReportCharacter,
    loadingType,
    errorMessage: submitError,
  } = useLoadCharacter({ characterId: characterId ?? "" });
  const isSubmitting = loadingType === CharacterFlow.REPORT_CHARACTER;

  const initialValues = useMemo(
    () => ({
      character_id: characterId || "",
    }),
    [characterId],
  );

  const submitForm = useCallback(async () => {
    setStateIsSubmitAttempted(true);
    const payload = Object.fromEntries(Object.entries(reportForm.values).filter(([, value]) => value !== null)) as unknown as IReportCharacterRequest;
    if (characterId) {
      payload.character_id = characterId;
    }
    if (isValid) {
      const { error } = await onUserReportCharacter(payload);
      if (error) return;
      const successMessage = t("chatbot:reportPage.form.successMessage");
      setStateSuccessMessage(successMessage);
      setStateIsFormSubmitted(true);
      displayToast({ message: successMessage, type: NotificationType.success });
    }
  }, [characterId, onUserReportCharacter, reportForm, isValid, t, displayToast]);

  return (
    <PageLayout size={PageSize.Medium}>
      <PageStaticMetaTags pageId="report" />
      <ReportHeader itemType="character" />
      <ReportForm
        reportForm={reportForm}
        initialValues={initialValues}
        submitForm={submitForm}
        itemId={characterId ?? ""}
        itemData={character}
        isSubmitAttempted={stateIsSubmitAttempted}
        setStateIsSubmitAttempted={setStateIsSubmitAttempted}
      />
      <ReportFooter
        submitForm={submitForm}
        successMessage={stateSuccessMessage}
        isFormSubmitted={stateIsFormSubmitted}
        isValid={isValid}
        hasFormChanges={hasFormChanges}
        isSubmitting={isSubmitting}
        submitError={submitError}
        itemType="character"
        isSubmitAttempted={stateIsSubmitAttempted}
      />
    </PageLayout>
  );
};
export default Report;
